import STATUS from 'app/redux/features/pad-initialization/constants/status.constant.js';

export function generateCode() {
  const alpha = 'abcdefghijklmnopqrstuvwxyz';
  const digit = () => Math.floor(Math.random() * 10);
  const firstLetter = alpha[Math.floor(Math.random() * alpha.length)];
  return `${firstLetter}${digit()}${digit()}${digit()}`;
}

/**
 * Default structure for an Event in the calendar visit schedule.
 * Represents a scheduled visit with a professional and datetime.
 */
export const DEFAULT_EVENT = {
  key: generateCode(),
  professionalId: null,
  professional: {},
  dateTime: null,
  padPrescriptionIds: []
};

/**
 * Factory function that creates an Event instance.
 * Ensures all event objects have the correct structure.
 *
 * @param {Object} data - Event data
 * @param {number} data.professionalId - ID of the professional
 * @param {Object} data.professional - Professional object (name, etc.)
 * @param {string} data.dateTime - ISO datetime string
 * @param {Array<number>} data.padPrescriptionIds - Array of prescription IDs
 * @returns {Object} A new Event instance
 */
export const createEvent = data => {
  if (!data) {
    return { ...DEFAULT_EVENT };
  }

  return {
    key: data.key || generateCode(),
    professionalId: data.professionalId || null,
    professional: data.professional || {},
    dateTime: data.dateTime || null,
    padPrescriptionIds: data.padPrescriptionIds || []
  };
};

/**
 * Validates an event object structure.
 *
 * @param {Object} event - Event to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidEvent = event => {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const hasProfessionalId = event.professionalId !== undefined && event.professionalId !== null;
  const hasDateTime = event.dateTime !== undefined && event.dateTime !== null;
  const hasProfessional = event.professional !== undefined;

  return hasProfessionalId && hasDateTime && hasProfessional;
};

/**
 * Default structure for a CalendarVisitSchedule.
 * Centralizes the schema definition to ensure consistency across the application.
 */
export const DEFAULT_CALENDAR_VISIT_SCHEDULE = {
  id: null,
  padVisitId: null,
  padVisit: null,
  previousEvents: [],
  events: [],
  automaticRepeat: false,
  prescriptionEventsSchedule: [],
  prescriptionLoadSchedule: [],
  dailyAutomaticRepeat: false,
  canStepFoward: false,
  status: STATUS.IDLE,
  error: null
};

/**
 * Cria uma instância de CalendarVisitSchedule usando apenas padVisit
 * @param {Object} padVisit - Objeto padVisit utilizado para construir o schedule
 * @returns {Object} CalendarVisitSchedule
 */
export const buildCalendarVisitSchedule = padVisit => {
  if (!padVisit) {
    return { ...DEFAULT_CALENDAR_VISIT_SCHEDULE };
  }

  return {
    ...DEFAULT_CALENDAR_VISIT_SCHEDULE,
    id: padVisit.id,
    padVisitId: padVisit.id,
    padVisit
  };
};

/**
 * Cria uma instância de CalendarVisitSchedule usando apenas padVisit
 * @param {Object} padVisit - Objeto padVisit utilizado para construir o schedule
 * @returns {Object} CalendarVisitSchedule
 */
export const createCalendarVisitSchedule = calendarVisitSchedule => {
  if (!calendarVisitSchedule) {
    return { ...DEFAULT_CALENDAR_VISIT_SCHEDULE };
  }

  const { padVisit, events } = calendarVisitSchedule;
  const padVisitId = calendarVisitSchedule.id || null;
  const id = padVisitId;

  return {
    id,
    padVisitId,
    padVisit,
    previousEvents: calendarVisitSchedule.previousEvents || [],
    events: Array.isArray(events) ? calendarVisitSchedule.events.map(createEvent) : [],
    prescriptionEventsSchedule: [],
    prescriptionLoadSchedule: calendarVisitSchedule.prescriptionLoadSchedule || [],
    automaticRepeat: calendarVisitSchedule.automaticRepeat || false,
    dailyAutomaticRepeat: calendarVisitSchedule.dailyAutomaticRepeat || false,
    canStepFoward: false,
    status: STATUS.IDLE,
    error: null
  };
};

/**
 * Cria uma instância de CalendarVisitSchedule a partir de prescription schedule
 * @param {Object} prescriptionSchedule - O prescription schedule
 * @returns {Object} CalendarVisitSchedule
 */
export const createVisitPrescriptionSchedule = prescriptionSchedule => {
  if (!prescriptionSchedule) {
    return { ...DEFAULT_CALENDAR_VISIT_SCHEDULE };
  }

  const { padVisit, events } = prescriptionSchedule;
  const padVisitId = prescriptionSchedule.id || null;
  const id = padVisitId;

  return {
    id,
    padVisitId,
    padVisit,
    previousEvents: [],
    events: Array.isArray(events) ? prescriptionSchedule.events.map(createEvent) : [],
    prescriptionEventsSchedule: [],
    prescriptionLoadSchedule: prescriptionSchedule.prescriptionLoadSchedule || [],
    automaticRepeat: prescriptionSchedule.automaticRepeat || false,
    dailyAutomaticRepeat: prescriptionSchedule.dailyAutomaticRepeat || false,
    canStepFoward: false,
    status: STATUS.IDLE,
    error: null
  };
};
